import React, { useState, useRef, useCallback, useMemo } from 'react'
import { ArrowRightThicker, LocationMap, Passenger, SpeakerPhoneCircle, TravelInfo } from '@enuygun/icons'
import { useTranslation } from 'next-i18next'
import { Button } from '@enuygun/design-system'
import { JustSignUpDialog } from '@enuygun/frontend-components'

import Slider from 'components/Slider'
import SnapCarousel from 'components/SnapCarousel'
import { useAppState } from 'context/AppContext'
import { useIsMobile } from 'hooks/useIsMobile'
import { useDeviceType } from 'hooks/useDeviceType'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'

import * as S from './WhyChooseRegister.styled'
import { IWhyChooseRegisterProps } from './WhyChooseRegister.types'

const WhyChooseRegister: React.FC<IWhyChooseRegisterProps> = () => {
  const { t, i18n } = useTranslation('common')
  const [showSignUpDialog, setShowSignUpDialog] = useState<boolean>(false)
  const isMobile = useIsMobile()
  const { isTablet } = useDeviceType()
  const isRTL = i18n?.dir() === 'rtl'

  const { market } = useAppState()
  const whyChooseRegisterContainerRef = useRef<HTMLDivElement>(null)

  const isWingie = market !== 'tr'

  const iconMapping = useMemo(
    () => ({
      savePassenger: <Passenger width={60} height={60} />,
      accessTravel: <TravelInfo width={60} height={60} />,
      viewReservations: <LocationMap width={60} height={60} />,
      memberDeals: <SpeakerPhoneCircle width={60} height={60} />,
    }),
    []
  )

  const benefits = useMemo(
    () => [
      {
        icon: iconMapping.savePassenger,
        titleKey: 'whyChooseRegister.benefits.savePassenger.title',
        contentKey: 'whyChooseRegister.benefits.savePassenger.content',
        iconType: 'savePassenger',
      },
      {
        icon: iconMapping.accessTravel,
        titleKey: 'whyChooseRegister.benefits.accessTravel.title',
        contentKey: 'whyChooseRegister.benefits.accessTravel.content',
        iconType: 'accessTravel',
      },
      {
        icon: iconMapping.viewReservations,
        titleKey: 'whyChooseRegister.benefits.viewReservations.title',
        contentKey: 'whyChooseRegister.benefits.viewReservations.content',
        iconType: 'viewReservations',
      },
      {
        icon: iconMapping.memberDeals,
        titleKey: 'whyChooseRegister.benefits.memberDeals.title',
        contentKey: 'whyChooseRegister.benefits.memberDeals.content',
        iconType: 'memberDeals',
      },
    ],
    [iconMapping]
  )

  // WhyChooseRegister button click tracking
  const handleButtonClick = useCallback(() => {
    setShowSignUpDialog(true)

    const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
      page: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
      type: 'click',
      message: 'why_choose_register_button_click',
      environment: environment,
      requestId,
      buttonText: t('whyChooseRegister.button') || null,
      isDialogOpened: true,
      isMobile: !!(isMobile || isTablet),
      market: market,
    })
  }, [isMobile, isTablet, market, isWingie, t])

  const renderWhyChooseRegisterCards = () =>
    benefits.map(({ icon, titleKey, contentKey, iconType }, index) => (
      <S.WhyChooseRegisterCard key={iconType} data-testid={`wingie-homepage-whyChooseRegisterWingie-${index}`}>
        {icon}
        <div>
          <S.WhyChooseRegisterCardHeading>{t(titleKey)}</S.WhyChooseRegisterCardHeading>
          <S.WhyChooseRegisterCardText>{t(contentKey)}</S.WhyChooseRegisterCardText>
        </div>
      </S.WhyChooseRegisterCard>
    ))

  return (
    <>
      <JustSignUpDialog show={showSignUpDialog} setShow={() => setShowSignUpDialog(false)} showCorporateSection={false} />
      <S.WhyChooseRegisterContainer ref={whyChooseRegisterContainerRef} data-testid="wingie-homepage-whyChooseRegister-container">
        <S.Content>
          <S.WhyChooseRegisterHeading data-testid="wingie-homepage-whyChooseRegister-heading">{t('whyChooseRegister.heading')}</S.WhyChooseRegisterHeading>
          {isMobile ? (
            <SnapCarousel
              options={{
                perPage: 1.34,
                gap: 16,
                hasLastChildPadding: true,
              }}
              dataTestId="whyUseRegisterWingie-carousel"
              containerWidth={1120}
              isMobile={true}
              isRTL={isRTL}
            >
              {renderWhyChooseRegisterCards()}
            </SnapCarousel>
          ) : (
            <Slider
              options={{
                perPage: 4,
                arrows: false,
                gap: 16,
                padding: 16,
                pagination: false,
                drag: false,
              }}
              dataTestId="whyChooseRegisterWingie-slider"
              isRTL={isRTL}
            >
              {renderWhyChooseRegisterCards()}
            </Slider>
          )}

          <S.WhyChooseRegisterButtonContainer>
            <Button onClick={handleButtonClick}>
              <S.WhyChooseRegisterButton>
                <div>{t('whyChooseRegister.button')}</div>
                <ArrowRightThicker width={20} height={20} />
              </S.WhyChooseRegisterButton>
            </Button>
          </S.WhyChooseRegisterButtonContainer>
        </S.Content>
      </S.WhyChooseRegisterContainer>
    </>
  )
}

export default WhyChooseRegister
